// One side of a live handoff (docs/adr/0010, docs/crypto-spec.md §5).
//
//   room socket ──► hellos ──► session (code) ──► WebRTC data channel, or the room as fallback
//
// The room (a WebSocket to the Worker) seats us and passes sealed blobs to
// the other side. When both are seated it says so, and each side sends a
// hello; from the two hellos @sss/crypto derives the session. The sender
// then offers a direct WebRTC channel, with the signalling sealed inside
// records. Records that matter (secret, delivered, acknowledged) wait until
// a transport is settled: the data channel if it opens in time, otherwise
// the room. With a paid relay (docs/adr/0013) the sender's offer also hands
// the recipient a TURN server, so the "direct" channel can go through
// Cloudflare's relay when the two networks can't reach each other. Never
// log anything from here: keys, records, SDP and relay credentials included.

import { CryptoError, fromBase64Url, startHandoff, toBase64Url, type HandoffHandshake, type HandoffRecordType, type HandoffSession } from '@sss/crypto';
import type { RelayIceServer, RoomClientFrame, RoomEndReason, RoomRole } from '@sss/types';
import { blobKind, encodeSignal, parseServerFrame, parseSignal, selectedPairUsesRelay, type Signal } from './handoffProtocol';

export type Transport = 'direct' | 'relay';

export interface HandoffEvents {
  /** We hold our seat. `expiresAt` is the room's deadline. */
  joined(expiresAt: string): void;
  /** The other side arrived or left. A new arrival means a new handshake and code. */
  peer(present: boolean, expiresAt: string): void;
  /** The session is up; show the code. */
  secure(code: string): void;
  transport(transport: Transport): void;
  /** The data channel opened; `viaRelay` if it goes through the TURN relay rather than straight between the browsers. */
  path?(viaRelay: boolean): void;
  /** A record from the other side (signal records are handled here). */
  record(type: Exclude<HandoffRecordType, 'signal'>, body: Uint8Array): void;
  ended(reason: RoomEndReason): void;
  /** Recipient: the room can't be joined. */
  unavailable(): void;
  /** The connection to the room is gone and couldn't be restored. */
  lost(): void;
}

export interface HandoffOptions {
  roomId: string;
  roomKey: Uint8Array;
  role: RoomRole;
  /** Sender only. */
  senderToken?: string;
  socketUrl: string;
  iceServers: RTCIceServer[];
  /** Sender only: a paid TURN relay credential for this side. */
  relay?: RelayIceServer;
  /**
   * Sender only: the recipient's own credential for the same relay, which
   * the offer carries to them. One each lets Cloudflare's usage show which
   * side relayed (docs/adr/0013). Without one, the offer carries `relay`.
   */
  peerRelay?: RelayIceServer;
  events: HandoffEvents;
  /** How long the sender waits for a direct channel before using the room. */
  directTimeoutMs?: number;
  /**
   * Sender only: keep this side's IP address from the other side (docs/adr/0014).
   * Without a paid relay, skip WebRTC and use the room; with one, gather only
   * relay candidates so the channel always goes through the TURN server.
   */
  hideAddress?: boolean;
}

const DIRECT_TIMEOUT_MS = 10_000;
/** A socket still connecting after this is given up on and retried (browsers allow one connecting socket per host). */
const CONNECT_TIMEOUT_MS = 10_000;
const RECONNECT_DELAYS_MS = [1_000, 2_000, 4_000];
const CHANNEL_LABEL = 'sss-handoff';
/** Bulk sends (file chunks) pause while the data channel has this much queued, and resume below the low mark. */
const CHANNEL_HIGH_BYTES = 4 * 1024 * 1024;
const CHANNEL_LOW_BYTES = 1024 * 1024;
/** Through the room, bulk sends pause while the socket has this much queued. A WebSocket has no low-water event, so it's polled. */
const SOCKET_HIGH_BYTES = 4 * 1024 * 1024;
const SOCKET_POLL_MS = 25;

const hasWebRtc = (): boolean => typeof RTCPeerConnection !== 'undefined';

export class HandoffConnection {
  private ws: WebSocket | null = null;
  private handshake: HandoffHandshake | null = null;
  private session: HandoffSession | null = null;
  /** A hello that arrived before our own handshake was ready. */
  private earlyHello: Uint8Array | null = null;
  /** Records that arrived while our session was still being derived. */
  private earlyRecords: { bytes: Uint8Array; via: Transport }[] = [];
  private pc: RTCPeerConnection | null = null;
  private channel: RTCDataChannel | null = null;
  private pendingCandidates: RTCIceCandidateInit[] = [];
  private transport: Transport | null = null;
  /** Records waiting for the sender's transport to settle. */
  private outbox: Uint8Array[] = [];
  private directTimer: ReturnType<typeof setTimeout> | undefined;
  /** Bumped per pairing, so results of an older pairing's async work are dropped. */
  private pairing = 0;
  private reconnects = 0;
  private finished = false;

  constructor(private readonly options: HandoffOptions) {}

  connect(): void {
    if (this.finished) return;
    const ws = new WebSocket(this.options.socketUrl);
    this.ws = ws;
    const stuck = setTimeout(() => {
      if (ws.readyState === WebSocket.CONNECTING) ws.close();
    }, CONNECT_TIMEOUT_MS);
    ws.addEventListener('open', () => {
      clearTimeout(stuck);
      if (this.options.role === 'sender') this.sendFrame({ t: 'auth', token: this.options.senderToken ?? '' });
    });
    ws.addEventListener('message', (event) => void this.onFrame(event.data));
    ws.addEventListener('close', () => {
      clearTimeout(stuck);
      if (this.ws === ws) this.onSocketClosed();
    });
  }

  /** Seal a record for the other side. The sender's wait for a settled transport; the recipient's go at once. */
  async send(type: Exclude<HandoffRecordType, 'signal'>, body?: Uint8Array): Promise<void> {
    const session = this.session;
    if (!session) throw new Error('No session');
    const record = await session.seal(type, body);
    if (this.transport || this.options.role === 'recipient') this.deliver(record);
    else this.outbox.push(record);
  }

  /** Like `send`, then wait until the transport's queue drains (file chunks). */
  async sendBulk(type: 'chunk', body: Uint8Array): Promise<void> {
    await this.send(type, body);
    if (this.transport === 'relay') {
      const ws = this.ws;
      while (ws && ws === this.ws && ws.readyState === WebSocket.OPEN && ws.bufferedAmount > SOCKET_HIGH_BYTES) {
        await new Promise((resolve) => setTimeout(resolve, SOCKET_POLL_MS));
      }
      return;
    }
    const channel = this.channel;
    if (this.transport !== 'direct' || !channel || channel.readyState !== 'open' || channel.bufferedAmount <= CHANNEL_HIGH_BYTES) return;
    await new Promise<void>((resolve) => {
      const done = () => {
        channel.removeEventListener('bufferedamountlow', done);
        channel.removeEventListener('close', done);
        resolve();
      };
      channel.addEventListener('bufferedamountlow', done);
      channel.addEventListener('close', done);
    });
  }

  /** The transport this pairing settled on, if it has. */
  get settledTransport(): Transport | null {
    return this.transport;
  }

  /** Sender only: steer the room. */
  notify(t: 'delivered' | 'complete' | 'cancel' | 'active'): void {
    this.sendFrame({ t });
  }

  /** Stop everything. Safe to call more than once. */
  close(): void {
    this.finished = true;
    this.teardownPairing();
    const ws = this.ws;
    this.ws = null;
    ws?.close(1000, 'done');
  }

  private sendFrame(frame: RoomClientFrame): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(frame));
  }

  private async onFrame(data: unknown): Promise<void> {
    const frame = parseServerFrame(data);
    if (!frame || this.finished) return;
    const { events } = this.options;
    switch (frame.t) {
      case 'joined':
        this.reconnects = 0;
        events.joined(frame.expiresAt);
        return;
      case 'peer':
        this.teardownPairing();
        events.peer(frame.present, frame.expiresAt);
        if (frame.present) await this.startPairing();
        return;
      case 'relay': {
        let bytes: Uint8Array;
        try {
          bytes = fromBase64Url(frame.d);
        } catch {
          return;
        }
        await this.onBytes(bytes, 'relay');
        return;
      }
      case 'ended':
        this.close();
        events.ended(frame.reason);
        return;
      case 'unavailable':
        this.close();
        events.unavailable();
        return;
      case 'error':
        // The room refused something we sent. Only a bug or tampering gets
        // here; the socket closes and the reconnect logic takes over.
        return;
    }
  }

  private onSocketClosed(): void {
    if (this.finished) return;
    const delay = RECONNECT_DELAYS_MS[this.reconnects];
    // An open data channel doesn't need the room, but without the room the
    // sender can't steer it; give up after a few tries either way.
    if (delay === undefined) {
      this.close();
      this.options.events.lost();
      return;
    }
    this.reconnects += 1;
    setTimeout(() => this.connect(), delay);
  }

  private async startPairing(): Promise<void> {
    const pairing = this.pairing;
    const handshake = await startHandoff(this.options.roomKey, this.options.roomId, this.options.role);
    if (pairing !== this.pairing || this.finished) return;
    this.handshake = handshake;
    this.sendFrame({ t: 'relay', d: toBase64Url(handshake.hello) });
    const early = this.earlyHello;
    this.earlyHello = null;
    if (early) await this.onHello(early);
  }

  private async onBytes(bytes: Uint8Array, via: Transport): Promise<void> {
    const kind = blobKind(bytes);
    if (kind === 'hello' && via === 'relay') await this.onHello(bytes);
    else if (kind === 'record') await this.onRecord(bytes, via);
  }

  private async onHello(hello: Uint8Array): Promise<void> {
    if (!this.handshake) {
      this.earlyHello = hello;
      return;
    }
    const pairing = this.pairing;
    let session: HandoffSession;
    try {
      session = await this.handshake.finish(hello);
    } catch {
      // Not from this pairing (or not genuine); keep waiting for the right one.
      return;
    }
    if (pairing !== this.pairing || this.finished) return;
    this.session = session;
    this.options.events.secure(session.code);
    if (this.options.role === 'sender') this.offerDirect();
    const early = this.earlyRecords;
    this.earlyRecords = [];
    for (const record of early) await this.onRecord(record.bytes, record.via);
  }

  private async onRecord(bytes: Uint8Array, via: Transport): Promise<void> {
    const session = this.session;
    if (!session) {
      // The other side finished its handshake first and is already signalling.
      if (this.handshake) this.earlyRecords.push({ bytes, via });
      return;
    }
    const pairing = this.pairing;
    let opened;
    try {
      opened = await session.open(bytes);
    } catch (e) {
      // Replays and forgeries are dropped; anything else is a bug worth surfacing.
      if (e instanceof CryptoError) return;
      throw e;
    }
    if (pairing !== this.pairing || this.finished) return;
    if (opened.type === 'signal') {
      const signal = parseSignal(opened.body);
      if (signal) await this.onSignal(signal);
      return;
    }
    // The recipient learns the transport from how the secret arrived.
    if (!this.transport) this.settle(via);
    this.options.events.record(opened.type, opened.body);
  }

  /** Sender: open a data channel and send the offer, or go straight to the room without WebRTC. */
  private offerDirect(): void {
    if (!hasWebRtc() || (this.options.hideAddress && !this.options.relay)) {
      this.settle('relay');
      return;
    }
    this.directTimer = setTimeout(() => this.settle('relay'), this.options.directTimeoutMs ?? DIRECT_TIMEOUT_MS);
    const { relay } = this.options;
    const peerRelay = this.options.peerRelay ?? relay;
    const pc = this.createPeerConnection(relay);
    this.watchChannel(pc, pc.createDataChannel(CHANNEL_LABEL, { ordered: true }));
    void (async () => {
      const pairing = this.pairing;
      await pc.setLocalDescription(await pc.createOffer());
      const description = pc.localDescription?.toJSON() as RTCSessionDescriptionInit | undefined;
      if (pairing === this.pairing && description) await this.sendSignal(peerRelay ? { description, relay: peerRelay } : { description });
    })().catch(() => this.settle('relay'));
  }

  private async onSignal(signal: Signal): Promise<void> {
    const { role } = this.options;
    if ('unsupported' in signal) {
      if (role === 'sender') this.settle('relay');
      return;
    }
    if ('candidate' in signal) {
      if (this.pc?.remoteDescription) await this.pc.addIceCandidate(signal.candidate).catch(() => undefined);
      else this.pendingCandidates.push(signal.candidate);
      return;
    }
    const { description } = signal;
    try {
      if (role === 'recipient' && description.type === 'offer') {
        if (!hasWebRtc()) {
          await this.sendSignal({ unsupported: true });
          return;
        }
        const pc = this.pc ?? this.createPeerConnection('relay' in signal ? signal.relay : undefined);
        pc.addEventListener('datachannel', (event) => this.watchChannel(pc, event.channel));
        await pc.setRemoteDescription(description);
        await this.flushCandidates();
        await pc.setLocalDescription(await pc.createAnswer());
        if (pc.localDescription) await this.sendSignal({ description: pc.localDescription.toJSON() as RTCSessionDescriptionInit });
      } else if (role === 'sender' && description.type === 'answer' && this.pc) {
        await this.pc.setRemoteDescription(description);
        await this.flushCandidates();
      }
    } catch {
      if (role === 'sender') this.settle('relay');
      else await this.sendSignal({ unsupported: true });
    }
  }

  private createPeerConnection(relay?: RelayIceServer): RTCPeerConnection {
    const { iceServers, role, hideAddress } = this.options;
    const pc = new RTCPeerConnection({
      iceServers: relay ? [...iceServers, relay] : iceServers,
      // Host and STUN candidates carry our addresses; relay candidates carry only the TURN server's.
      iceTransportPolicy: role === 'sender' && hideAddress ? 'relay' : 'all',
    });
    this.pc = pc;
    pc.addEventListener('icecandidate', (event) => {
      if (event.candidate) void this.sendSignal({ candidate: event.candidate.toJSON() });
    });
    pc.addEventListener('connectionstatechange', () => {
      if (pc.connectionState === 'failed' && this.options.role === 'sender') this.settle('relay');
    });
    return pc;
  }

  private watchChannel(pc: RTCPeerConnection, channel: RTCDataChannel): void {
    channel.binaryType = 'arraybuffer';
    channel.bufferedAmountLowThreshold = CHANNEL_LOW_BYTES;
    this.channel = channel;
    channel.addEventListener('open', () => {
      if (this.options.role === 'sender') this.settle('direct');
      void this.checkPath(pc);
    });
    channel.addEventListener('message', (event) => {
      if (event.data instanceof ArrayBuffer) void this.onBytes(new Uint8Array(event.data), 'direct');
    });
  }

  /** Learn whether the open channel goes through the relay. */
  private async checkPath(pc: RTCPeerConnection): Promise<void> {
    const pairing = this.pairing;
    let viaRelay: boolean;
    try {
      viaRelay = selectedPairUsesRelay([...(await pc.getStats()).values()] as Record<string, unknown>[]);
    } catch {
      return;
    }
    if (pairing !== this.pairing || this.finished) return;
    this.options.events.path?.(viaRelay);
  }

  private async flushCandidates(): Promise<void> {
    const candidates = this.pendingCandidates;
    this.pendingCandidates = [];
    for (const candidate of candidates) await this.pc?.addIceCandidate(candidate).catch(() => undefined);
  }

  private async sendSignal(signal: Signal): Promise<void> {
    const session = this.session;
    if (!session) return;
    this.sendFrame({ t: 'relay', d: toBase64Url(await session.seal('signal', encodeSignal(signal))) });
  }

  /** Fix the transport for this pairing (first decision wins) and send whatever was waiting. */
  private settle(transport: Transport): void {
    if (this.transport || !this.session) return;
    clearTimeout(this.directTimer);
    this.transport = transport;
    this.options.events.transport(transport);
    const waiting = this.outbox;
    this.outbox = [];
    for (const record of waiting) this.deliver(record);
  }

  private deliver(record: Uint8Array): void {
    if (this.transport === 'direct' && this.channel?.readyState === 'open') this.channel.send(new Uint8Array(record));
    else this.sendFrame({ t: 'relay', d: toBase64Url(record) });
  }

  private teardownPairing(): void {
    this.pairing += 1;
    clearTimeout(this.directTimer);
    this.channel?.close();
    this.pc?.close();
    this.channel = null;
    this.pc = null;
    this.pendingCandidates = [];
    this.handshake = null;
    this.session = null;
    this.earlyHello = null;
    this.earlyRecords = [];
    this.transport = null;
    this.outbox = [];
  }
}
